import * as React from 'react'

import { Layer, Source, useMap } from 'react-map-gl/mapbox'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  hiddenWhen,
  IS_SELECTED,
  LETTER_SPACING_EM,
  SELECTED_LABEL_PLACEMENT,
  SEMIBOLD_LABEL_FONT,
} from '~/client/src/shared/map/labelLayerStyle'

import {
  SHARED_PINS_CHIP_LAYER,
  SHARED_PINS_CHIP_SELECTED_LAYER,
  SHARED_PINS_LAYER,
} from '../MapBoxViewer/mapboxConstants'
import {
  MAX_ITEM_ZOOM_LEVEL,
  PROJECT_MARKER_ZOOM_LEVEL,
} from '../MapBoxViewer/MapBoxViewer.store'
import { ICON_TEXT_OCCLUSION_PAINT } from '../MapBoxViewer/mapOcclusionRule'
import { useCulledPinKeys } from '../MapBoxViewer/useCulledPinKeys'
import { useResendDataAfterLoad } from '../MapBoxViewer/useResendDataAfterLoad'
import {
  SHARED_CHIP_FIT,
  SHARED_CHIP_IMAGE_ID,
} from '../SitemapHelpers/utils/GLSharedChipImage'
import GLWorkflowPinLayer, { workflowPinLayerIds } from './GLWorkflowPinLayer'
import { IWorkflowPinPoint } from './workflowPinPoints'

import Colors from '~/client/src/shared/theme.module.scss'

export const SHARED_PINS_LAYER_ID = SHARED_PINS_LAYER
export const SHARED_CHIP_LAYER_ID = SHARED_PINS_CHIP_LAYER
const SHARED_CHIP_SOURCE_ID = `${SHARED_PINS_LAYER_ID}-chip-source`
export const SHARED_PIN_LAYER_IDS = workflowPinLayerIds(SHARED_PINS_LAYER_ID)

// The map label voice, shared with the pin and location tiers: the plate is a label that
// happens to sit on a solid ground rather than a casing.
const CHIP_FONT = SEMIBOLD_LABEL_FONT
const CHIP_TEXT_SIZE = 12
const CHIP_LINE_HEIGHT_PX = 16
// Below the pin's own tip, which sits on the coordinate — the DOM chip hung off the pin's
// bottom edge by a similar gap.
const CHIP_DROP_PX = 6

// icon-text-fit hangs the plate around the shaped text by the fixed slice on each side, and
// those two are not equal, so the plate's centre lands left of the word's. Nudging the text
// right by half the difference puts the PLATE under the pin, which is what the eye lines up.
// Both insets are constants, so nothing has to measure the label.
const CHIP_CENTERING_EM =
  (SHARED_CHIP_FIT.leadPx - SHARED_CHIP_FIT.trailPx) / 2 / CHIP_TEXT_SIZE

// Fitting width alone leaves the plate its drawn height, centred on the line box — so it
// reaches half the difference ABOVE the line. The gap has to be measured to that edge: offset
// by the gap alone and the plate overlaps the pin, and a chip that collides with the pin it
// captions is dropped by the very decluttering it needs.
const CHIP_HALF_OVERHANG_PX = Math.max(
  0,
  (SHARED_CHIP_FIT.fixedHeightPx - CHIP_LINE_HEIGHT_PX) / 2,
)
const CHIP_DROP_EM = (CHIP_DROP_PX + CHIP_HALF_OVERHANG_PX) / CHIP_TEXT_SIZE

// Stable identity: a fresh array each render would rebind the pin layer's listeners.
export const CHIP_INTERACTIVE_LAYER_IDS = [
  SHARED_CHIP_LAYER_ID,
  SHARED_PINS_CHIP_SELECTED_LAYER,
]

const CHIP_PAINT = {
  'text-color': Colors.mapWhite,
  ...ICON_TEXT_OCCLUSION_PAINT,
}
const RESTING_CHIP_PAINT = { ...CHIP_PAINT, ...hiddenWhen(IS_SELECTED) }

const EMPTY_FC: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
}

interface IProps {
  points: IWorkflowPinPoint[]
  isZoomScalingEnabled?: boolean
  // Passed to the pins only; the chip hangs from the tip at its own size.
  iconScale?: number
  markerScale?: number
  interactive?: boolean
  onHoverChange?: (hovered: boolean, source: string) => void
  onPinClick?: (coordKey: string) => void
  // Passed to the pins only; the chips keep decluttering.
  isOverlapForced?: boolean
}

// Workflows and deliveries shared in from another project: the same pin art as this project's
// own, greyed by isShared, plus the [icon Shared] chip that told them apart in the DOM. One
// component so a host mounts both halves and their offsets stay together.
const GLSharedPinLayer: React.FC<IProps> = ({
  points,
  isZoomScalingEnabled = false,
  iconScale,
  markerScale,
  interactive = true,
  onHoverChange,
  onPinClick,
  isOverlapForced = false,
}) => {
  const { current: map } = useMap()
  const captionAnchors = React.useMemo(
    () =>
      points.map(p => ({
        key: p.coordKey,
        longitude: p.longitude,
        latitude: p.latitude,
      })),
    [points],
  )
  const culledPinKeys = useCulledPinKeys(
    map,
    SHARED_PIN_LAYER_IDS,
    captionAnchors,
  )
  const chipData = React.useMemo<GeoJSON.FeatureCollection>(() => {
    const captioned = culledPinKeys.size
      ? points.filter(p => !culledPinKeys.has(p.coordKey))
      : points
    if (!captioned.length) {
      return EMPTY_FC
    }

    return {
      type: 'FeatureCollection',
      features: captioned.map(p => ({
        type: 'Feature' as const,
        properties: { coordKey: p.coordKey, selected: p.isSelected },
        geometry: {
          type: 'Point' as const,
          coordinates: [p.longitude, p.latitude],
        },
      })),
    }
  }, [points, culledPinKeys])
  useResendDataAfterLoad(map, SHARED_CHIP_SOURCE_ID, chipData)

  // Memoised: react-map-gl diffs a layout key by key unless its identity holds. Keyed on the text,
  // read here so a language switch still reaches it.
  const sharedText = Localization.translator.shared
  const { chipLayout, chipTwinLayout } = React.useMemo(() => {
    const layout = {
      'text-field': sharedText,
      'text-font': CHIP_FONT,
      'text-size': CHIP_TEXT_SIZE,
      'text-line-height': CHIP_LINE_HEIGHT_PX / CHIP_TEXT_SIZE,
      'text-letter-spacing': LETTER_SPACING_EM,
      // The pin's tip is the coordinate, so the chip hangs below it.
      'text-anchor': 'top',
      'text-offset': [CHIP_CENTERING_EM, CHIP_DROP_EM],
      'icon-image': SHARED_CHIP_IMAGE_ID,
      // Width only: the plate's corners and its glyph leave no row to stretch.
      'icon-text-fit': 'width',
      // Fitting width centres the whole IMAGE on the line box, and the image carries
      // its shadow room below the plate and none above — so the plate rides half that
      // padding high until the quad is pushed back down by it.
      'icon-offset': [0, SHARED_CHIP_FIT.shadowRecenterPx],
    }
    return {
      chipLayout: layout,
      chipTwinLayout: { ...layout, ...SELECTED_LABEL_PLACEMENT },
    }
  }, [sharedText])

  // The chip is a caption, so enforceShapeLayerOrder holds it under every location layer with
  // the other captions. GL places the topmost layer first, so the pins and locations claim their
  // space before any chip is considered: no pin is suppressed by a caption, and a chip that lands
  // under a pin or another chip drops out. Exempting the chip from placement instead — the
  // obvious fix for the suppressed pin — silently disabled decluttering between chips, because a
  // symbol that ignores placement registers no box for the next one to collide with.
  return (
    <>
      <Source
        id={SHARED_CHIP_SOURCE_ID}
        type="geojson"
        data={chipData as never}
      >
        <Layer
          id={SHARED_CHIP_LAYER_ID}
          type="symbol"
          minzoom={PROJECT_MARKER_ZOOM_LEVEL}
          maxzoom={MAX_ITEM_ZOOM_LEVEL}
          layout={chipLayout as never}
          paint={RESTING_CHIP_PAINT as never}
        />
        <Layer
          id={SHARED_PINS_CHIP_SELECTED_LAYER}
          type="symbol"
          minzoom={PROJECT_MARKER_ZOOM_LEVEL}
          maxzoom={MAX_ITEM_ZOOM_LEVEL}
          filter={IS_SELECTED as never}
          layout={chipTwinLayout as never}
          paint={CHIP_PAINT as never}
        />
      </Source>
      <GLWorkflowPinLayer
        layerId={SHARED_PINS_LAYER_ID}
        points={points}
        isZoomScalingEnabled={isZoomScalingEnabled}
        iconScale={iconScale}
        markerScale={markerScale}
        interactive={interactive}
        onHoverChange={onHoverChange}
        onPinClick={onPinClick}
        extraInteractiveLayerIds={CHIP_INTERACTIVE_LAYER_IDS}
        isOverlapForced={isOverlapForced}
      />
    </>
  )
}

export default React.memo(GLSharedPinLayer)
